// src/components/settings/MasterDataSections.js
// Session 74u. The company-wide taxonomies, governed from Company Settings at
// admin/executive. Stations are deliberately NOT here — they are branch-local
// and live in User Management, because a branch manager governs their own
// branch's list and cannot open this page at all. See StationGovernance.js.
//
// This header read "the two company-wide taxonomies" until Session 78n5 and had
// been wrong since 78n2. There are FOUR in this file: categories, departments,
// units and denial reasons. Corrected rather than left, for the same reason as
// taxonomy.js's own header — an undercount is how a later session concludes a
// list is ungoverned and builds a second surface for it.
//
// #262 gap (3) and #198's departments half; extended at 78n2 (#398, units) and
// 78n5 (#397, denial reasons).

import { useState, useEffect, useCallback } from 'react';
import {
  fetchCategories, addCategory, renameCategory, setCategoryActive,
  fetchDepartments, addDepartment, renameDepartment, setDepartmentActive,
  fetchUnits, addUnit, updateUnit, setUnitActive, countUnitUsage,
  fetchDenialReasons, addDenialReason, renameDenialReason, setDenialReasonActive,
  countDenialReasonUsage,
  fetchTaxonomyLog, countDepartmentUsage,
} from '../../lib/taxonomy';
import {
  SectionHeading, TaxonomyGroupHeader, TaxonomyRow, EmptyParentNotice,
  TaxonomyListShell, TaxonomySheet, AddButton, ChangeLogStrip,
} from './TaxonomyManager';

export function CategoryGovernance() {
  const [rows, setRows] = useState([]);
  const [log, setLog] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  // A failed load must not render as an empty list. Silently-empty is the
  // failure mode this project keeps getting bitten by -- an empty screen that
  // looks like "nothing here yet" and is actually "the query broke".
  const load = useCallback(async () => {
    const [cats, logRows] = await Promise.all([
      fetchCategories(),
      fetchTaxonomyLog('category'),
    ]);
    if (cats.error) { setError('Could not load categories: ' + cats.error.message); return; }
    setRows(cats.rows);
    setLog(logRows.rows);
  }, []);

  useEffect(() => { load(); }, [load]);

  const parents = rows.filter(r => !r.parent_id);
  const childrenOf = id => rows.filter(r => r.parent_id === id);
  // Counted active-only, because that is what the pickers see. A parent whose
  // only subcategory is deactivated is as unusable as one with none at all, and
  // saying "1 subcategory" there would disagree with the picker's own
  // "No subcategories yet" placeholder on the very next screen.
  const activeChildrenOf = id => rows.filter(r => r.parent_id === id && r.is_active !== false);

  async function handleSubmit(name) {
    setSaving(true);
    setError('');
    let result;
    if (sheet.mode === 'rename') {
      result = await renameCategory(sheet.row.id, name);
    } else {
      result = await addCategory(name, sheet.parentId);
    }
    setSaving(false);
    if (result.error) { setError(result.error.message); return; }
    setSheet(null);
    await load();
  }

  async function handleToggle(row) {
    setBusyId(row.id);
    setError('');
    const { error: err } = await setCategoryActive(row.id, row.is_active === false, rows);
    setBusyId(null);
    if (err) { setError(err.message); return; }
    await load();
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <SectionHeading>Item categories</SectionHeading>

      <p style={{ fontSize: 12, color: '#5C5C5C', margin: '0 0 12px', lineHeight: 1.5 }}>
        Every catalog item is filed under a subcategory. Parents are groups only — an item is never
        filed directly on one. Categories are never deleted, only deactivated, because catalog items
        reference them permanently.
      </p>

      {error && (
        <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <TaxonomyListShell>
        {parents.map(parent => {
          const kids = childrenOf(parent.id);
          const activeKids = activeChildrenOf(parent.id);
          return (
            <div key={parent.id}>
              <TaxonomyGroupHeader
                label={parent.name}
                meta={activeKids.length > 0 ? `${activeKids.length} subcategor${activeKids.length === 1 ? 'y' : 'ies'}` : null}
                isActive={parent.is_active !== false}
                busy={busyId === parent.id}
                onRename={() => { setError(''); setSheet({ mode: 'rename', row: parent }); }}
                onToggleActive={() => handleToggle(parent)}
              />
              {activeKids.length === 0 && parent.is_active !== false && <EmptyParentNotice />}
              {kids.map(kid => (
                <TaxonomyRow
                  key={kid.id}
                  name={kid.name}
                  indent
                  isActive={kid.is_active !== false}
                  busy={busyId === kid.id}
                  onRename={() => { setError(''); setSheet({ mode: 'rename', row: kid }); }}
                  onToggleActive={() => handleToggle(kid)}
                />
              ))}
            </div>
          );
        })}
      </TaxonomyListShell>

      <div style={{ display: 'flex', gap: 8 }}>
        <AddButton label="Add category" onClick={() => { setError(''); setSheet({ mode: 'add', parentId: null }); }} />
        <AddButton
          label="Add subcategory"
          onClick={() => { setError(''); setSheet({ mode: 'add', parentId: parents[0]?.id ?? null }); }}
        />
      </div>

      <ChangeLogStrip rows={log} />

      {sheet && (
        <TaxonomySheet
          title={
            sheet.mode === 'rename' ? 'Rename category'
              : sheet.parentId ? 'Add subcategory'
                : 'Add category'
          }
          confirmLabel={sheet.mode === 'rename' ? 'Rename' : 'Add'}
          initialName={sheet.mode === 'rename' ? sheet.row.name : ''}
          extraFields={
            sheet.mode === 'add' && sheet.parentId !== null ? (
              <div style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 500, color: '#0F0F0F', margin: '0 0 6px' }}>Under which category</p>
                <select
                  value={sheet.parentId}
                  onChange={e => setSheet(s => ({ ...s, parentId: Number(e.target.value) }))}
                  style={{ width: '100%', height: 44, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, background: 'white' }}
                >
                  {parents.filter(p => p.is_active !== false).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            ) : null
          }
          onCancel={() => { setSheet(null); setError(''); }}
          onSubmit={handleSubmit}
          saving={saving}
          serverError={error}
        />
      )}
    </div>
  );
}

export function DepartmentGovernance() {
  const [rows, setRows] = useState([]);
  const [log, setLog] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const [depts, logRows] = await Promise.all([
      fetchDepartments(),
      fetchTaxonomyLog('department'),
    ]);
    if (depts.error) { setError('Could not load departments: ' + depts.error.message); return; }
    setRows(depts.rows);
    setLog(logRows.rows);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Built once per open sheet rather than inline, so TaxonomySheet's effect does
  // not see a new function identity on every keystroke and refetch the counts.
  const loadConsequence = useCallback(async () => {
    if (!sheet || sheet.mode !== 'rename') return null;
    const counts = await countDepartmentUsage(sheet.row.name);
    const parts = [
      counts.users ? `${counts.users} ${counts.users === 1 ? 'person' : 'people'}` : null,
      counts.tools ? `${counts.tools} tool${counts.tools === 1 ? '' : 's'}` : null,
      counts.stations ? `${counts.stations} station${counts.stations === 1 ? '' : 's'}` : null,
      counts.inspections ? `${counts.inspections} inspection${counts.inspections === 1 ? '' : 's'}` : null,
    ].filter(Boolean);
    if (parts.length === 0) return `Nothing currently uses ${sheet.row.name}, so this rename affects only the list itself.`;
    return `This also updates ${parts.join(', ')} currently using ${sheet.row.name}.`;
  }, [sheet]);

  async function handleSubmit(name) {
    setSaving(true);
    setError('');
    const result = sheet.mode === 'rename'
      ? await renameDepartment(sheet.row.id, name)
      : await addDepartment(name);
    setSaving(false);
    if (result.error) { setError(result.error.message); return; }
    setSheet(null);
    await load();
  }

  async function handleToggle(row) {
    setBusyId(row.id);
    setError('');
    const { error: err } = await setDepartmentActive(row.id, row.is_active === false);
    setBusyId(null);
    if (err) { setError(err.message); return; }
    await load();
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <SectionHeading>Departments</SectionHeading>

      <p style={{ fontSize: 12, color: '#5C5C5C', margin: '0 0 12px', lineHeight: 1.5 }}>
        One company-wide list, used by people, tools, stations and inspections. Deactivating a
        department removes it from those pickers; anyone already assigned to it keeps the assignment.
      </p>

      {error && (
        <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <TaxonomyListShell>
        {rows.map(row => (
          <TaxonomyRow
            key={row.id}
            name={row.name}
            isActive={row.is_active !== false}
            busy={busyId === row.id}
            onRename={() => { setError(''); setSheet({ mode: 'rename', row }); }}
            onToggleActive={() => handleToggle(row)}
          />
        ))}
      </TaxonomyListShell>

      <AddButton label="Add department" onClick={() => { setError(''); setSheet({ mode: 'add' }); }} />

      <ChangeLogStrip rows={log} />

      {sheet && (
        <TaxonomySheet
          title={sheet.mode === 'rename' ? 'Rename department' : 'Add department'}
          confirmLabel={sheet.mode === 'rename' ? 'Rename' : 'Add'}
          initialName={sheet.mode === 'rename' ? sheet.row.name : ''}
          loadConsequence={sheet.mode === 'rename' ? loadConsequence : null}
          onCancel={() => { setSheet(null); setError(''); }}
          onSubmit={handleSubmit}
          saving={saving}
          serverError={error}
        />
      )}
    </div>
  );
}

// Session 78n2, #398. The fourth governed taxonomy, and the first that answers
// two questions instead of one.
//
// Rendered as two groups over ONE table rather than as two tables, because
// three words — each, box and roll — genuinely belong to both lists. Vinyl is
// stocked by the roll and ships by the roll; the census found box on 2 real
// production catalog items. A row whose kind is 'both' therefore appears under
// both headings on purpose, keyed per group so React sees two distinct rows.
//
// Kind and code are edited through the same sheet as the name and saved in one
// update, not three — see updateUnit(). There is no delete: the database
// refuses one on a unit in use, and retiring is what governance actually wants.
export function UnitGovernance() {
  const [rows, setRows] = useState([]);
  const [log, setLog] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [sheetKind, setSheetKind] = useState('measure');
  const [sheetCode, setSheetCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  // A failed load must not render as an empty list — the silently-empty failure
  // mode this project keeps getting bitten by. An empty units list would read as
  // "no units yet" and would actually mean every unit dropdown in the app is
  // about to be blank.
  const load = useCallback(async () => {
    const [units, logRows] = await Promise.all([
      fetchUnits(),
      fetchTaxonomyLog('unit'),
    ]);
    if (units.error) { setError('Could not load units: ' + units.error.message); return; }
    setRows(units.rows);
    setLog(logRows.rows);
  }, []);

  useEffect(() => { load(); }, [load]);

  const stocking = rows.filter(r => r.kind === 'measure' || r.kind === 'both');
  const packaging = rows.filter(r => r.kind === 'package' || r.kind === 'both');

  function openSheet(next) {
    setError('');
    setSheetKind(next.mode === 'rename' ? next.row.kind : 'measure');
    setSheetCode(next.mode === 'rename' ? (next.row.code || '') : '');
    setSheet(next);
  }

  // Built once per open sheet rather than inline, so TaxonomySheet's effect does
  // not see a new function identity on every keystroke and refetch the counts.
  const loadConsequence = useCallback(async () => {
    if (!sheet || sheet.mode !== 'rename') return null;
    const counts = await countUnitUsage(sheet.row.name);
    const parts = [
      counts.items ? `${counts.items} catalog item${counts.items === 1 ? '' : 's'}` : null,
      counts.vendorPrices ? `${counts.vendorPrices} vendor price${counts.vendorPrices === 1 ? '' : 's'}` : null,
    ].filter(Boolean);
    if (parts.length === 0) {
      return `Nothing currently uses ${sheet.row.name}, so this rename affects only the list itself.`;
    }
    return `This also updates ${parts.join(' and ')} currently using ${sheet.row.name}.`;
  }, [sheet]);

  async function handleSubmit(name) {
    setSaving(true);
    setError('');
    const result = sheet.mode === 'rename'
      ? await updateUnit(sheet.row.id, { name, code: sheetCode, kind: sheetKind })
      : await addUnit(name, sheetKind, sheetCode);
    setSaving(false);
    if (result.error) { setError(result.error.message); return; }
    setSheet(null);
    await load();
  }

  async function handleToggle(row) {
    setBusyId(row.id);
    setError('');
    const { error: err } = await setUnitActive(row.id, row.is_active === false);
    setBusyId(null);
    if (err) { setError(err.message); return; }
    await load();
  }

  function rowMeta(row) {
    const bits = [];
    if (row.code) bits.push(row.code);
    if (row.kind === 'both') bits.push('in both lists');
    return bits.length ? bits.join(' · ') : null;
  }

  function renderGroup(list, groupKey) {
    return list.map(row => (
      <TaxonomyRow
        key={`${groupKey}-${row.id}`}
        name={row.name}
        meta={rowMeta(row)}
        isActive={row.is_active !== false}
        busy={busyId === row.id}
        onRename={() => openSheet({ mode: 'rename', row })}
        onToggleActive={() => handleToggle(row)}
      />
    ));
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <SectionHeading>Units</SectionHeading>

      <p style={{ fontSize: 12, color: '#5C5C5C', margin: '0 0 12px', lineHeight: 1.5 }}>
        Two lists, because they answer two different questions. A <strong>stocking unit</strong> says
        what one of something is &mdash; a sheet, a gallon &mdash; and is set once when an item is
        added to the catalog. A <strong>package type</strong> says how a vendor ships it &mdash; a
        case, a pallet &mdash; and is set per vendor on the price sheet. Both come from the
        international trade standard, and the code beside each entry is that standard&rsquo;s code
        where one exists. Deactivating a unit removes it from the pickers; anything already using it
        keeps it.
      </p>

      {error && (
        <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <TaxonomyListShell>
        <TaxonomyGroupHeader label="Stocking units" meta="what one of these is" />
        {renderGroup(stocking, 'm')}
        <TaxonomyGroupHeader label="Package types" meta="how the vendor ships it" />
        {renderGroup(packaging, 'p')}
      </TaxonomyListShell>

      <AddButton label="Add unit" onClick={() => openSheet({ mode: 'add' })} />

      <ChangeLogStrip rows={log} />

      {sheet && (
        <TaxonomySheet
          title={sheet.mode === 'rename' ? 'Edit unit' : 'Add unit'}
          confirmLabel={sheet.mode === 'rename' ? 'Save' : 'Add'}
          initialName={sheet.mode === 'rename' ? sheet.row.name : ''}
          loadConsequence={sheet.mode === 'rename' ? loadConsequence : null}
          extraFields={
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>
                  Which list
                </div>
                <select
                  value={sheetKind}
                  onChange={e => setSheetKind(e.target.value)}
                  style={{
                    width: '100%', height: 44, border: '1px solid #E0E0E0', borderRadius: 8,
                    padding: '0 12px', fontSize: 16, background: 'white', color: '#0F0F0F',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="measure">Stocking unit &mdash; what one of these is</option>
                  <option value="package">Package type &mdash; how the vendor ships it</option>
                  <option value="both">Both</option>
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>
                  Standard code (optional)
                </div>
                <input
                  type="text"
                  value={sheetCode}
                  onChange={e => setSheetCode(e.target.value)}
                  placeholder="e.g. EA, FTK. Leave blank if unknown."
                  style={{
                    width: '100%', height: 44, border: '1px solid #E0E0E0', borderRadius: 8,
                    padding: '0 12px', fontSize: 16, boxSizing: 'border-box',
                  }}
                />
              </div>
            </>
          }
          onCancel={() => { setSheet(null); setError(''); }}
          onSubmit={handleSubmit}
          saving={saving}
          serverError={error}
        />
      )}
    </div>
  );
}

// Session 78n5, #397, gate decision D8. The fifth governed taxonomy.
//
// Two things make this one different from the four above it, and both are
// deliberate rather than incidental.
//
// FIRST, the usage count renders on the row, not only in a confirm dialog.
// Gate callout I: the count is the point of having codes instead of a text box
// at all. "Available at another branch, used 11 times" is the business case for
// inter-branch transfers (#371) stated in one line, on a screen an executive
// already opens. The other four taxonomies show usage only when you are about
// to break something with it; this one shows it because it is the answer.
//
// SECOND, a rename here does NOT reach the records that use it, and the confirm
// step says so out loud. Renaming a department rewrites five tables; renaming a
// denial reason rewrites nothing, because every past denial froze the wording it
// was given. That is the opposite promise from the one this same sheet makes
// three sections up, so leaving it unsaid would be actively misleading.
export function DenialReasonGovernance() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [log, setLog] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  // A failed load must not render as an empty list — the silently-empty failure
  // mode this project keeps getting bitten by. An empty reasons list would read
  // as "none configured yet" and would actually mean the Deny sheet's required
  // field can never be satisfied and nobody can deny a request at all.
  const load = useCallback(async () => {
    const [reasons, logRows] = await Promise.all([
      fetchDenialReasons(),
      fetchTaxonomyLog('denial_reason'),
    ]);
    if (reasons.error) { setError('Could not load denial reasons: ' + reasons.error.message); return; }
    setRows(reasons.rows);
    setLog(logRows.rows);
    setCounts(await countDenialReasonUsage(reasons.rows.map(r => r.id)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadConsequence = useCallback(async () => {
    if (!sheet || sheet.mode !== 'rename') return null;
    const used = counts[sheet.row.id];
    if (used === null || used === undefined) {
      return 'Past denials keep the wording they were given — renaming this changes only what future denials are offered.';
    }
    if (used === 0) {
      return 'No denial has used this reason yet, so this rename affects only the list itself.';
    }
    return `${used} past denial${used === 1 ? '' : 's'} used this reason and ${used === 1 ? 'it keeps' : 'they keep'} the original wording. `
      + 'Renaming changes only what future denials are offered.';
  }, [sheet, counts]);

  async function handleSubmit(name) {
    setSaving(true);
    setError('');
    const result = sheet.mode === 'rename'
      ? await renameDenialReason(sheet.row.id, name)
      : await addDenialReason(name);
    setSaving(false);
    if (result.error) { setError(result.error.message); return; }
    setSheet(null);
    await load();
  }

  async function handleToggle(row) {
    setBusyId(row.id);
    setError('');
    const { error: err } = await setDenialReasonActive(row.id, row.is_active === false);
    setBusyId(null);
    if (err) { setError(err.message); return; }
    await load();
  }

  // null means the count query failed, which is not the same as zero. Zero is a
  // real answer here and reads "Not used yet", so rendering a failure as zero
  // would state something false about the business rather than merely omit it.
  function usageMeta(row) {
    const used = counts[row.id];
    if (used === null || used === undefined) return null;
    if (used === 0) return 'Not used yet';
    return `Used ${used} time${used === 1 ? '' : 's'}`;
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <SectionHeading>Denial reasons</SectionHeading>

      <p style={{ fontSize: 12, color: '#5C5C5C', margin: '0 0 12px', lineHeight: 1.5 }}>
        When a manager denies a material request they must pick one of these, and may add a note.
        Picking from a list rather than typing is what makes denials countable &mdash; the usage
        figures below are the evidence for decisions like opening branch transfers. A reason is
        never deleted, only retired: it stops being offered on new denials while past ones still
        read exactly as they were written.
      </p>

      {error && (
        <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <TaxonomyListShell>
        {rows.map(row => (
          <TaxonomyRow
            key={row.id}
            name={row.name}
            meta={usageMeta(row)}
            isActive={row.is_active !== false}
            busy={busyId === row.id}
            onRename={() => { setError(''); setSheet({ mode: 'rename', row }); }}
            onToggleActive={() => handleToggle(row)}
          />
        ))}
      </TaxonomyListShell>

      <AddButton label="Add a reason" onClick={() => { setError(''); setSheet({ mode: 'add' }); }} />

      <ChangeLogStrip rows={log} />

      {sheet && (
        <TaxonomySheet
          title={sheet.mode === 'rename' ? 'Rename denial reason' : 'Add a denial reason'}
          confirmLabel={sheet.mode === 'rename' ? 'Rename' : 'Add'}
          initialName={sheet.mode === 'rename' ? sheet.row.name : ''}
          loadConsequence={sheet.mode === 'rename' ? loadConsequence : null}
          onCancel={() => { setSheet(null); setError(''); }}
          onSubmit={handleSubmit}
          saving={saving}
          serverError={error}
        />
      )}
    </div>
  );
}
